import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import _ from 'lodash';
import makeStyles from '@mui/styles/makeStyles';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Container from '@mui/material/Container';

import { SearchInput } from 'widgets-v6/standard-input';
import { SimpleSelect } from 'widgets-v6/select';

import { AudienceCard } from '../modules/audience-card';
import { AudienceTable } from '../modules/audience-table';

const sortingOptions = [
    {
        label: 'Sort by: Name',
        value: 'name',
        direction: 'asc',
    },
    {
        label: 'Sort by: Newest',
        value: '_created',
        direction: 'desc',
    },
    {
        label: 'Sort by: Last Updated',
        value: 'lastRefreshed',
        direction: 'desc',
    },
];

const useStyles = makeStyles(() => ({
    image: {
        width: '85%',
    },
    iconWrapper: {
        display: 'inline-flex',
        height: '112px',
        width: '112px',
        backgroundColor: '#fff',
        borderRadius: '50%',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

const PermutiveLayout = ({
    audienceSegments,
    onSearch,
    segmentCount,
    activeAudience,
    organizationId,
}) => {
    const classes = useStyles();
    const [activeSort, setActiveSort] = useState({ value: 'name', direction: 'asc' });

    const debouncedSearch = useMemo(() => {
        return _.debounce(value => onSearch(value), 200);
    }, []);

    const handleSearch = e => {
        const { value } = e.target;
        debouncedSearch(value);
    };

    const handleSortChange = event => {
        const { value, direction } = _.find(sortingOptions, { value: event.target.value });
        setActiveSort({ value, direction });
    };

    const sortedAudiences = _.orderBy(audienceSegments, [activeSort.value], [activeSort.direction]);

    const linkToAudienceTab = {
        pathname: '/audiences/all',
        query: { audienceTab: 'dmp' },
    };

    return (
        <Box padding={4}>
            <Breadcrumbs aria-label="breadcrumb">
                <RouterLink
                    color="inherit"
                    component={Link}
                    to={linkToAudienceTab}
                    underline="none"
                >
                    DMP Integrations
                </RouterLink>
                <Typography color="textPrimary">Permutive</Typography>
            </Breadcrumbs>
            <Container>
                <Grid container>
                    <Grid item xs={2} alignItems="center" style={{ textAlign: 'center' }}>
                        <Box className={classes.iconWrapper}>
                            <img className={classes.image} src="/images/PermutiveImage.png" />
                        </Box>
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant="h3" gutterBottom>
                            Permutive
                        </Typography>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            Segments pushed from Permutive will appear here.
                        </Typography>
                        <Grid container>
                            <Grid item>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Total
                                </Typography>
                                <Typography variant="h6">{segmentCount}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Box mb={2.5} mt={5}>
                    <Grid container justifyContent="space-between">
                        <Grid item xs={3}>
                            <SearchInput placeholder="Filter Audiences" onChange={handleSearch} />
                        </Grid>
                        <Grid item xs={3}>
                            <SimpleSelect
                                options={sortingOptions}
                                value={activeSort.value}
                                onChange={handleSortChange}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <AudienceTable
                    audiences={sortedAudiences}
                    activeAudience={activeAudience}
                    renderContent={segment => (
                        <AudienceCard
                            segment={segment}
                            active={segment.id === activeAudience}
                            organizationId={organizationId}
                        />
                    )}
                />
            </Container>
        </Box>
    );
};

export default PermutiveLayout;
